/**
 * A teacher's tests for an assignment (2026-10-01): the list on an assignment's Evaluation tab,
 * the dialog that adds one, and what a grade's report says each one found.
 *
 * > Owner: "run the test as a confirmation is do everything corretly! can be by text and the
 * > agent write the tests or can be passing the test, and the agent only adapt to the singatures
 * > and do the necessary teeks if needed!"
 *
 * 🔴 THEY CONFIRM; THEY DO NOT SCORE. Nothing here moves a point. A failing test reaches the mark
 * only as the grader's own lost-point item naming it, and the report puts the two side by side.
 * 🔴 THE GRADER MAY RENAME, NEVER RE-ASSERT. The report shows every change it made to a teacher's
 * file, and whether the console found the assertions intact — the server's verdict, not this
 * page's.
 * 🔴 A LIST OF CARDS AND A DIALOG, NOT A FORM IN EVERY ROW (owner, 2026-10-05: *"the tests with a
 * better UX UI!"*). The editor used to open each test in place, with three add buttons, two
 * paragraphs of rules and its own Save under the list. A card now says what a test checks and
 * how it runs; adding or changing one is a dialog; the rules are behind a Why; and the panel's one
 * save bar saves the tests with everything else on the assignment.
 * 🔴 A TEST LIVES INSIDE THE CRITERION IT PROVES, AND NAMES THE TASK (owner, same day: *"the add
 * test should be by criteria and those tests will impact that criteria … to each test we
 * associated a task"*). The grader is told both, must charge a failure to that criterion, and the
 * console says so when it did not (`teacher_tests.placement`).
 */
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  type ApiError,
  getTeacherTests,
  putTeacherTests,
  type Report,
  type TeacherTest,
  type TeacherTestResult,
  type TeacherTestsView,
} from "../api";
import { tracked, useAsync } from "../useAsync";
import { Badge, ErrorBox, When, Why } from "../ui";

/** How many test functions a file holds: `#[test]` in Rust, `it(`/`test(` in TypeScript. */
export function testsIn(t: TeacherTest): number {
  const src = t.content ?? "";
  return (t.path ?? "").endsWith(".ts")
    ? (src.match(/(^|[^\w.])(it|test)\s*\(/g) ?? []).length
    : (src.match(/#\[test\]/g) ?? []).length;
}

/** The names of the test functions a file holds, as written: the `fn` after `#[test]` in Rust,
 *  the string given to `it(`/`test(` in TypeScript. A file has no description of its own, so
 *  these are what its card says it checks. A name this cannot read is left out, never guessed. */
export function testNames(t: TeacherTest): string[] {
  const src = t.content ?? "";
  if ((t.path ?? "").endsWith(".ts")) {
    return [...src.matchAll(/(?:^|[^\w.])(?:it|test)\s*\(\s*(["'`])((?:(?!\1)[^\\\n]|\\.)*)\1/g)]
      .map((m) => m[2] ?? "").filter(Boolean);
  }
  return [...src.matchAll(/#\[test\](?:\s*#\[[^\]\n]*\])*\s*(?:pub\s+)?(?:async\s+)?fn\s+(\w+)/g)]
    .map((m) => m[1] ?? "").filter(Boolean);
}

const kb = (s = "") => `${(new TextEncoder().encode(s).length / 1024).toFixed(1)} KB`;
const language = (path = "") => (path.endsWith(".ts") ? "TypeScript" : "Rust");
const runner = (path = "") => path.endsWith(".ts")
  ? "Run with node:test against the student's program."
  : "Run with cargo test against the student's program, in LiteSVM.";

/** The server's own name for the generic test (`teacher_tests.BRIEF_NAME`); shown before the first
 *  save names it. One per criterion, so it does not say which: the criterion it sits under does. */
const BRIEF = "The essential tests";
const GLYPH: Record<TeacherTest["kind"], string> = { brief: "✓", words: "Aa", code: "{ }" };
/** The criterion a test bears on — the server's `teacher_tests.criterion_of`: its own, or `C1`
 *  for one saved before tests named one. */
const DEFAULT_CRITERION = "C1";
export const criterionOf = (t: TeacherTest) => (t.criterion ?? "").trim() || DEFAULT_CRITERION;
const nameOf = (t: TeacherTest) =>
  t.kind === "code" ? (t.path || "a test file") : t.kind === "brief" ? BRIEF : (t.name || "Untitled");

// ── the draft ───────────────────────────────────────────────────────────────────

/** A test in the draft: what the server stores, plus a key that is the draft's own. */
export type TestDraft = TeacherTest & { key: string };
let seq = 0;
const draftOf = (t: TeacherTest): TestDraft => ({ ...t, key: `d${++seq}` });
const strip = ({ key: _k, ...t }: TestDraft): TeacherTest => t;
const same = (a: TeacherTest, b: TeacherTest) =>
  a.kind === b.kind && a.name === b.name && (a.what ?? "") === (b.what ?? "")
  && (a.path ?? "") === (b.path ?? "") && (a.content ?? "") === (b.content ?? "")
  && criterionOf(a) === criterionOf(b) && (a.task ?? "") === (b.task ?? "");

/** An assignment's tests as they are being edited, and the one way to save them. */
export interface TestsDraft {
  view: TeacherTestsView | null;
  error: ApiError | null;
  reload: () => void;
  draft: TestDraft[];
  /** What differs from the saved version, in words: "1 test added", "2 tests changed". */
  changes: string[];
  add: (t: TeacherTest) => void;
  replace: (key: string, t: TeacherTest) => void;
  remove: (key: string) => void;
  /** Moves every test to the criterion `to` gives it, or removes it where `to` answers null —
   *  what removing an extra criterion does to the tests inside it and after it. */
  recriterion: (to: (criterion: string) => string | null) => void;
  discard: () => void;
  /** Saves the draft against the version it was opened on. Resolves to whether anything was
   *  saved; rejects with the server's refusal, leaving the draft as it was. */
  save: () => Promise<boolean>;
}

/**
 * 🔴 THE DRAFT LIVES IN THE PANEL, NOT IN THE LIST. The assignment's one save bar saves the tests
 * with the brief and the criteria, so the panel holds this and hands it to the list.
 * ⚠️ `base_version` IS THE VERSION THE DRAFT WAS OPENED ON, so a save over somebody else's newer
 * one is refused by the server rather than silently replacing it.
 */
export function useTeacherTests(courseId: string, cw: string): TestsDraft {
  const q = useAsync(() => getTeacherTests(courseId, cw), [courseId, cw]);
  const [base, setBase] = useState<TeacherTestsView | null>(null);
  const [draft, setDraft] = useState<TestDraft[]>([]);

  const changes = useMemo(() => {
    if (!base) return [];
    const saved = new Map(base.items.map((t) => [t.id ?? "", t]));
    const kept = new Set(draft.map((t) => t.id ?? ""));
    const added = draft.filter((t) => !t.id || !saved.has(t.id)).length;
    const edited = draft.filter((t) => {
      const was = t.id ? saved.get(t.id) : undefined;
      return was !== undefined && !same(was, t);
    }).length;
    const removed = base.items.filter((t) => !kept.has(t.id ?? "")).length;
    const say = (n: number, what: string) => (n ? [`${n} test${n === 1 ? "" : "s"} ${what}`] : []);
    return [...say(added, "added"), ...say(edited, "changed"), ...say(removed, "removed")];
  }, [base, draft]);

  // ⚠️ A FRESH READ REPLACES THE DRAFT ONLY WHILE NOTHING IS UNSAVED. Read through a ref, so the
  // effect runs on a new read and never on a keystroke.
  const dirty = useRef(false);
  dirty.current = changes.length > 0;
  useEffect(() => {
    if (q.data && !dirty.current) {
      setBase(q.data);
      setDraft(q.data.items.map(draftOf));
    }
  }, [q.data]);

  return {
    view: base, error: q.error, reload: q.reload, draft, changes,
    add: (t) => setDraft((d) => [...d, draftOf(t)]),
    replace: (key, t) => setDraft((d) => d.map((x) => (x.key === key ? { ...t, key } : x))),
    remove: (key) => setDraft((d) => d.filter((x) => x.key !== key)),
    recriterion: (to) => setDraft((d) => d.flatMap((x) => {
      const now = to(criterionOf(x));
      return now === null ? [] : now === criterionOf(x) ? [x] : [{ ...x, criterion: now }];
    })),
    discard: () => setDraft((base?.items ?? []).map(draftOf)),
    save: async () => {
      if (!base) return false;
      const r = await tracked(putTeacherTests(courseId, cw,
        { items: draft.map(strip), base_version: base.version }));
      dirty.current = false;
      setBase(r);
      setDraft(r.items.map(draftOf));
      q.reload();
      return Boolean(r.saved);
    },
  };
}

// ── the list ────────────────────────────────────────────────────────────────────

type Open = { mode: "add" } | { mode: "edit" | "show"; key: string };
/** A criterion a test may be put under: its id and what it is called on this assignment. */
export interface CriterionChoice {
  id: string; label: string;
  /** The tasks this criterion is split into, `T1 · name`, offered for "the task it proves". */
  tasks?: string[];
}

/**
 * The tests of ONE criterion, inside that criterion's row on the Evaluation tab — or, with no
 * `criterion`, the tests that are under none of `criteria` (a criterion that went away), which
 * are not run and say so.
 * ⚠️ READING IS FOR EVERYBODY. A teacher without Edit rubrics gets the same cards, and Show on a
 * file opens it read-only.
 */
export function TestList({ t, criterion, criteria, off = false }: {
  t: TestsDraft; criterion?: string; criteria: CriterionChoice[];
  /** The criterion is off here, so its tests are not run. */
  off?: boolean;
}) {
  const [open, setOpen] = useState<Open | null>(null);
  const v = t.view;
  if (!v) return null;
  const edit = v.may_edit;
  const d = t.draft;
  const known = new Set(criteria.map((c) => c.id));
  const mine = d.filter((x) => (criterion ? criterionOf(x) === criterion
                                          : !known.has(criterionOf(x))));
  if (!criterion && mine.length === 0) return null;
  const files = d.filter((x) => x.kind === "code").length;
  const editing = open && open.mode !== "add" ? d.find((x) => x.key === open.key) : undefined;
  // Which criteria already ask for the essential tests — once each — leaving out the one edited.
  const briefOn = new Set(d.filter((x) => x.kind === "brief" && x.key !== editing?.key)
    .map(criterionOf));

  const what = (x: TestDraft) => {
    if (x.kind === "brief") {
      return <div className="tt-card-what">The grader writes up to eight for what this criterion
        credits, with the failure paths that matter.</div>;
    }
    if (x.kind === "words") {
      return x.what?.trim()
        ? <div className="tt-card-what">{x.what}</div>
        /* ⚠️ A NAME WITH NOTHING UNDER IT is usually an instruction to choose the tests, which
           the grader cannot run as a test — said here, before a run is paid for. */
        : <div className="tt-card-what warn">Nothing says how to check this, so the grader
            cannot run it. Add a sentence — or let the grader choose the tests instead.</div>;
    }
    const names = testNames(x);
    return names.length > 0
      ? <div className="tt-card-what">Checks <span className="mono">{names.slice(0, 3).join(", ")}</span>
          {names.length > 3 && <> and {names.length - 3} more</>}</div>
      : null;
  };
  const meta = (x: TestDraft) => {
    if (x.kind === "brief") {
      return <>written by the grader{x.what?.trim() && <> · focus: {x.what}</>}</>;
    }
    if (x.kind === "words") return <>in words · the grader writes it for each student</>;
    const n = testsIn(x);
    return <>{language(x.path)} · {n} test{n === 1 ? "" : "s"} · {kb(x.content)}</>;
  };

  return (
    <div className={`tt-in${criterion ? "" : " stray"}`}>
      <div className="tt-in-head">
        {criterion
          ? <span className="tt-in-t">Tests{mine.length > 0 && <span className="muted"> · {mine.length}</span>}</span>
          : <span className="tt-in-t warn">Tests on no criterion of this assignment</span>}
        <span className="tt-in-m">{criterion
          ? (off ? "Off here, so these are not run." : "Each proves a task works.")
          : "They are not run. Move each under a criterion, or remove it."}</span>
        {criterion && (
          <Why label={`How the tests of ${criterion} count`}>
            Each test proves that a task works, and counts under this criterion only. It runs on
            every submission, on the commit being graded. When it fails, the grader takes the
            points off this criterion, naming the test and its task — how many is the grader's
            judgement — and the grade goes to you for review. A test in words: the grader writes
            it against the student's own program. A file: the grader adapts names, never what it
            checks; the console compares every assertion with yours.
          </Why>)}
        {criterion && edit && (
          <span className="tt-in-add">
            <button className="btn sm" type="button" disabled={d.length >= v.limits.tests}
                    aria-label={`Add a test to ${criterion}`}
                    onClick={() => setOpen({ mode: "add" })}>+ Add a test</button>
          </span>)}
      </div>

      {mine.length === 0
        ? <p className="small muted">No tests on {criterion}.</p>
        : (
          <ol className="tt-cards">
            {mine.map((x) => {
              const blank = x.kind === "words" && !x.what?.trim();
              return (
                <li key={x.key} className={`tt-card${blank ? " blank" : ""}`}>
                  <span className="tt-ico" aria-hidden="true">{GLYPH[x.kind]}</span>
                  <div className="tt-card-main">
                    <div className="tt-card-name">
                      {x.kind === "code" ? <code>{x.path}</code> : nameOf(x)}</div>
                    {x.task?.trim() && <div className="tt-card-task">Proves: {x.task}</div>}
                    {what(x)}
                    <div className="tt-card-meta">{meta(x)}</div>
                  </div>
                  <div className="tt-card-acts">
                    {edit ? (<>
                      <button className="btn sm ghost" type="button"
                              aria-label={`Edit ${nameOf(x)}`}
                              onClick={() => setOpen({ mode: "edit", key: x.key })}>
                        {x.kind === "brief" ? "Edit focus" : "Edit"}</button>
                      <button className="btn sm ghost" type="button"
                              aria-label={`Remove ${nameOf(x)}`}
                              onClick={() => t.remove(x.key)}>Remove</button>
                    </>) : x.kind === "code" && (
                      <button className="btn sm ghost" type="button"
                              aria-label={`Show ${nameOf(x)}`}
                              onClick={() => setOpen({ mode: "show", key: x.key })}>Show</button>)}
                  </div>
                </li>);
            })}
          </ol>)}

      {open && (open.mode === "add" || editing) && (
        <TestDialog
          mode={open.mode} initial={editing} limits={v.limits}
          criteria={criteria} criterion={editing ? criterionOf(editing) : (criterion ?? "")}
          briefOn={briefOn}
          files={files - (editing?.kind === "code" ? 1 : 0)}
          onClose={() => setOpen(null)}
          onSubmit={(x) => {
            if (editing) t.replace(editing.key, x); else t.add(x);
            setOpen(null);
          }} />)}
    </div>);
}

/** Every criterion's tests together: the version they were saved as, and the limits. */
export function TestsFoot({ t }: { t: TestsDraft }) {
  if (t.error) return <ErrorBox err={t.error} onRetry={t.reload} />;
  const v = t.view;
  if (!v) return <div className="skel" aria-label="Loading the tests" />;
  const files = t.draft.filter((x) => x.kind === "code").length;
  return (
    <div className="as-foot">
      <span>Tests: {v.version > 0
        ? <>version {v.version} · saved by {v.saved_by || "somebody"}, <When iso={v.saved_at} /></>
        : "not saved yet"}</span>
      <span>· {t.draft.length} of {v.limits.tests} tests · {files} of {v.limits.files} files</span>
      {v.history.length > 1 && (
        <details className="tt-history">
          <summary>History</summary>
          <ul>{v.history.map((h) => (
            <li key={h.version}>Version {h.version} · {h.tests} test{h.tests === 1 ? "" : "s"}
              {" · "}{h.saved_by || "somebody"}, <When iso={h.saved_at} /></li>))}</ul>
        </details>)}
    </div>);
}

// ── the dialog ──────────────────────────────────────────────────────────────────

const KINDS: { id: TeacherTest["kind"]; title: string; note: string }[] = [
  { id: "words", title: "Describe it",
    note: "Say what to check. The grader writes the test for each student's program." },
  { id: "code", title: "Upload a file",
    note: "A .rs or .ts test. Names are adapted per student; what it asserts never is." },
  { id: "brief", title: "Let the grader choose",
    note: "The essential tests for what this criterion credits, with the failure paths." },
];

/**
 * Adding, changing or reading one test.
 * ⚠️ A PLAIN OVERLAY, NOT `<dialog>` — the New team popup's reasons: `showModal` is uneven under
 * jsdom, so it is built like the app's other popovers. Escape, the scrim, Cancel and × each close
 * it, the first field takes the focus, and the focus goes back to whatever opened it.
 * ⚠️ IT CHANGES THE DRAFT, NEVER THE SERVER. The panel's save bar is what saves, so a test added
 * here is one more unsaved change until then.
 */
function TestDialog({ mode, initial, limits, criteria, criterion, briefOn, files, onSubmit,
                     onClose }: {
  mode: "add" | "edit" | "show";
  initial?: TeacherTest;
  limits: TeacherTestsView["limits"];
  /** The criteria a test may be put under, and the one it starts under. */
  criteria: CriterionChoice[];
  criterion: string;
  /** The criteria that already ask the grader for the essential tests, by another test. */
  briefOn: Set<string>;
  /** How many other tests are files. */
  files: number;
  onSubmit: (t: TeacherTest) => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<TeacherTest["kind"]>(initial?.kind ?? "words");
  const [name, setName] = useState(initial?.kind === "words" ? initial.name : "");
  const [what, setWhat] = useState(initial?.what ?? "");
  const [path, setPath] = useState(initial?.path ?? "tests/");
  const [content, setContent] = useState(initial?.content ?? "");
  const [crit, setCrit] = useState(criterion || criteria[0]?.id || DEFAULT_CRITERION);
  const [task, setTask] = useState(initial?.task ?? "");
  const choiceTasks = criteria.find((c) => c.id === crit)?.tasks ?? [];
  const first = useRef<HTMLElement | null>(null);
  const ids = useId();
  const read = mode === "show";

  // ⚠️ ONCE, ON OPENING. The parent passes a new `onClose` on every render, and an effect keyed
  // on it would move the focus back to the first field whenever the page behind re-rendered.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    first.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("keydown", esc);
      back?.focus?.();
    };
  }, []);

  const refused = (k: TeacherTest["kind"]) =>
    k === "brief" && briefOn.has(crit) ? `Already on ${crit}.`
    : k === "code" && files >= limits.files ? `${limits.files} files already — remove one first.`
    : "";
  const ready = refused(kind) === "" && (kind === "words" ? name.trim() !== ""
    : kind === "code" ? path.trim() !== "" && content.trim() !== "" : true);
  const load = (f: File | undefined) => {
    if (!f) return;
    void f.text().then((text) => {
      setContent(text);
      // The file's own name, unless a path has been typed already.
      setPath((p) => (p.trim() === "" || p.trim() === "tests/" ? `tests/${f.name}` : p));
    });
  };
  const submit = () => {
    const keep = { ...(initial?.id ? { id: initial.id } : {}), criterion: crit, task: task.trim() };
    onSubmit(kind === "words" ? { ...keep, kind, name: name.trim(), what: what.trim() }
      : kind === "code" ? { ...keep, kind, name: path.trim(), path: path.trim(), content }
      : { ...keep, kind, name: BRIEF, what: what.trim() });
  };
  const title = mode === "add" ? `Add a test to ${crit}` : read ? (initial ? nameOf(initial) : "A test")
    : kind === "brief" ? "The essential tests" : "Edit the test";
  const draftCode: TeacherTest = { kind: "code", name: path, path, content };

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal wide" role="dialog" aria-modal="true" aria-labelledby={`${ids}-h`}>
        <div className="modal-head">
          <strong id={`${ids}-h`}>{title}</strong>
          <span className="spacer" />
          <button className="btn ghost sm" type="button" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* 🔴 WHERE IT COUNTS, AND WHAT IT PROVES — what the grader is told about the test, and
              the criterion a failure is charged to. */}
          {read
            ? <p className="small muted">Counts under {crit}{task.trim() && <> · proves: {task}</>}</p>
            : (
              <div className="tt-where">
                <label className="as-field" htmlFor={`${ids}-crit`}>Counts under
                  <select id={`${ids}-crit`} className="select" value={crit}
                          onChange={(e) => setCrit(e.target.value)}>
                    {criteria.map((c) => (
                      <option key={c.id} value={c.id}>{c.id} · {c.label}</option>))}
                  </select></label>
                <label className="as-field" htmlFor={`${ids}-task`}>The task it proves (optional)
                  <input id={`${ids}-task`} className="input" type="text" value={task}
                         maxLength={160} placeholder="For example: Task 2 — transfer two tokens"
                         list={choiceTasks.length ? `${ids}-tasks` : undefined}
                         onChange={(e) => setTask(e.target.value)} /></label>
                {/* 🔴 THE ASSIGNMENT'S OWN TASKS, OFFERED, NOT IMPOSED (2026-10-07): picking one
                    names it the way the grader is told it, and free text still works. */}
                {choiceTasks.length > 0 && (
                  <datalist id={`${ids}-tasks`}>
                    {choiceTasks.map((x) => <option key={x} value={x} />)}
                  </datalist>)}
              </div>)}

          {mode === "add" && (
            <div className="tt-kinds" role="group" aria-label="Kind of test">
              {KINDS.map((k) => {
                const no = refused(k.id);
                return (
                  <button key={k.id} type="button" className="tt-kind" aria-pressed={kind === k.id}
                          disabled={no !== ""} onClick={() => setKind(k.id)}
                          ref={k.id === "words" ? (el) => { first.current = el; } : undefined}>
                    <b>{k.title}</b><span>{no || k.note}</span>
                  </button>);
              })}
            </div>)}

          {kind === "words" && (<>
            <label className="as-field" htmlFor={`${ids}-name`}>What it checks
              <input id={`${ids}-name`} className="input" type="text" value={name}
                     maxLength={160} placeholder="For example: a stranger cannot withdraw from a vault"
                     ref={mode === "edit" ? (el) => { first.current = el; } : undefined}
                     onChange={(e) => setName(e.target.value)} /></label>
            <label className="as-field" htmlFor={`${ids}-what`}>How to check it
              <textarea id={`${ids}-what`} className="ta prose short" rows={3} value={what}
                        maxLength={800}
                        placeholder="What to do, and what must then be true."
                        onChange={(e) => setWhat(e.target.value)} /></label>
          </>)}

          {kind === "brief" && (<>
            <p className="small muted">The grader reads what the assignment asks and writes the
              tests that prove each required behaviour works, with the failure paths that matter —
              at most eight — then runs them.</p>
            <label className="as-field" htmlFor={`${ids}-focus`}>What to focus on (optional)
              <textarea id={`${ids}-focus`} className="ta prose short" rows={2} value={what}
                        maxLength={800} placeholder="The withdraw path, the PDA seeds…"
                        ref={mode === "edit" ? (el) => { first.current = el; } : undefined}
                        onChange={(e) => setWhat(e.target.value)} /></label>
          </>)}

          {kind === "code" && (<>
            {!read && (
              <label className="as-field" htmlFor={`${ids}-path`}>Where it goes in the submission
                <input id={`${ids}-path`} className="input mono" type="text" value={path}
                       placeholder="tests/vault.rs"
                       ref={mode === "edit" ? (el) => { first.current = el; } : undefined}
                       onChange={(e) => setPath(e.target.value)} /></label>)}
            <label className="as-field" htmlFor={`${ids}-code`}>{read ? "The file" : "The test"}
              <textarea id={`${ids}-code`} className="ta tt-code" rows={14} spellCheck={false}
                        readOnly={read} value={content}
                        ref={read ? (el) => { first.current = el; } : undefined}
                        onChange={(e) => setContent(e.target.value)} /></label>
            <div className="as-hint">
              {runner(path)} {testsIn(draftCode)} test{testsIn(draftCode) === 1 ? "" : "s"}
              {" · "}{kb(content)}
              {!read && (
                <label className="tt-upload">Or load it from a file
                  <input type="file" accept={limits.suffixes.join(",")}
                         onChange={(e) => load(e.target.files?.[0])} /></label>)}
            </div>
          </>)}
        </div>
        <div className="modal-foot">
          {!read && <span className="small muted">Next runs only — nothing is re-graded.</span>}
          <span className="spacer" />
          <button className="btn ghost" type="button" onClick={onClose}>{read ? "Done" : "Cancel"}</button>
          {!read && (
            <button className="btn primary" type="button" disabled={!ready} onClick={submit}>
              {mode === "add" ? "Add test" : "Update test"}</button>)}
        </div>
      </div>
    </div>);
}

const RESULT: Record<string, { tone: "ok" | "stop" | "warn"; label: string; dot: string }> = {
  pass: { tone: "ok", label: "passed", dot: "ok" },
  fail: { tone: "stop", label: "failed", dot: "stop" },
  absent: { tone: "warn", label: "could not run", dot: "warn" },
};

const rowClass = (res: string) => (res === "pass" ? "ttr-row" : "ttr-row ttr-failed");

/**
 * The test itself, as the teacher gave it (owner, 2026-10-01: "it should show the tests").
 * ⚠️ A TEST IN WORDS WITH NO DESCRIPTION gave the grader nothing but its name, and one the grader
 * did nothing with says so — with the remedy, because the name was usually an instruction to
 * choose the tests, which is what the generic test is for.
 * 🔴 "DID NOTHING WITH" IS NOT "DID NOT RUN". [X] 2026-10-02: the grader read such a test as a
 * request for the essential tests, wrote three, and could run none of them — and this said "its
 * name was all the grader had" above the three tests it had written. `acted` is a run that
 * passed or failed, or a test it wrote.
 */
function Definition({ t, acted }: { t: TeacherTestResult; acted: boolean }) {
  if (t.kind === "code") {
    return t.given ? (
      <details className="rp-disc ttr-disc"><summary>Your file</summary>
        <pre className="md-pre"><code>{t.given}</code></pre></details>) : null;
  }
  if (t.what) {
    return <div className="ttr-def">{t.kind === "brief" ? "Focus" : "What it checks"}: {t.what}</div>;
  }
  if (t.kind === "words" && !acted) {
    return (
      <div className="ttr-def small warn">This test does not say what to check: its name was all
        the grader had. To have the grader choose the tests, use <strong>Let the grader write
        them</strong> on the assignment.</div>);
  }
  return null;
}

/** Where a failing test's points went: the grader's lost-point items that name it. */
function costOf(r: Report, id: string): { crit: string; points: number }[] {
  return r.per_criterion.flatMap((c) => (c.lost ?? [])
    .filter((l) => l.test === id)
    .map((l) => ({ crit: c.id, points: l.points })));
}

/** How many of the teacher's tests passed, against everything they gave — a test the grader
 *  never reported counts as given and not passed. One definition, read by every head that
 *  counts tests — a criterion's and the section for tests on none — so no two can disagree. */
export function testTally(rows: TeacherTestResult[]): { passed: number; total: number } {
  const runs = rows.flatMap((t) => t.runs);
  const passed = runs.filter((x) => x.result === "pass").length;
  const missing = rows.filter((t) => t.status === "not_reported").length;
  return { passed, total: runs.length + missing };
}

/** The head of a list of tests: how many passed, and one mark per run. */
function TestsHead({ rows, id, title, sub, inCard }: {
  rows: TeacherTestResult[]; id: string; title: string; sub?: ReactNode; inCard?: boolean;
}) {
  const { passed, total } = testTally(rows);
  // ⚠️ Inside a card the criterion's own title is the h3, so this is a level below it.
  const H = inCard ? "h4" : "h3";
  return (
    <div className="ttr-head">
      <H className="ttr-title" id={id}>{title}</H>
      <span className="ttr-sub">{passed} of {total} passed{sub}</span>
      <span className="spacer" />
      <span className="ttr-tally" role="img" aria-label={`${passed} passed, ${total - passed} did not`}>
        {rows.flatMap((t) => (t.status === "not_reported"
          ? [<i key={t.id} className="warn" />]
          : t.runs.map((x, j) => <i key={`${t.id}-${j}`} className={RESULT[x.result]?.dot ?? "warn"} />)))}
      </span>
    </div>);
}

/**
 * The rows of a list of the teacher's tests: a file is one row per test function in it, and a
 * test the grader never reported is a row of its own.
 *
 * 🔴 A TEST THE GRADER NEVER REPORTED IS SHOWN, AS NOT RUN. Leaving it out would make a skipped
 * test look exactly like a set nobody wrote.
 * 🔴 "COUNTED UNDER C1" IS A PLAIN LINK TO THE CARD OF C1 (owner, 2026-10-03). The report stacks
 * every criterion at `#crit-<id>`, so the browser follows it, and a Ctrl-, Cmd- or middle click
 * opens it in a new tab: nothing here handles a click, so nothing can cancel one.
 * ⚠️ `here` IS THE CARD THE LIST SITS IN. A link from a card to itself goes nowhere, so there the
 * criterion is not repeated and a cost charged to it reads "this criterion" — and a cost charged
 * anywhere else is still a link, because that is the placement the grade escalates over.
 */
function TestRows({ r, rows, here }: { r: Report; rows: TeacherTestResult[]; here?: string }) {
  // 🔴 WHERE IT COUNTS AND WHAT IT PROVES (2026-10-05), as the run was given them — a plain link
  // to the criterion's card, the same as "Counted under". A grade from before says neither.
  const where = (t: TeacherTestResult) => {
    const task = t.task ? <>proves: {t.task}{" · "}</> : null;
    if (!t.criterion) return null;
    if (t.criterion === here) return task;
    return <><a href={`#crit-${t.criterion}`}>{t.criterion}</a>{t.task && <> · proves: {t.task}</>}{" · "}</>;
  };
  const meta = (t: TeacherTestResult) => {
    if (t.kind === "words") return <>{where(t)}in words · the grader wrote this test</>;
    if (t.kind === "brief") return <>{where(t)}from what the criterion credits · the grader wrote it</>;
    const n = t.changes.length;
    return (<>
      {where(t)}<code>{t.path}</code>{" · "}
      {n ? `adapted to this program: ${n} change${n === 1 ? "" : "s"}` : "run as you wrote it"}
      {t.integrity === "unchanged" && <> · <span className="ttr-ok">assertions unchanged</span></>}
      {t.integrity === "changed" && <> · <span className="ttr-bad">assertions changed</span></>}
      {t.integrity === "unverified" && <> · <span className="ttr-warn">assertions not checked</span></>}
    </>);
  };
  return (
    <ul className="ttr-rows">
      {rows.map((t) => {
        const cost = costOf(r, t.id);
        if (t.status === "not_reported") {
          return (
            <li className="ttr-row" key={t.id}>
              <span className="ttr-res"><Badge tone="warn">not run</Badge></span>
              <div className="ttr-main">
                <div className="ttr-name">{t.kind === "code" ? <code>{t.path}</code> : t.name}</div>
                <div className="ttr-meta">{where(t)}The grader did not run this test, or did
                  not report it, so it confirmed nothing.</div>
                <Definition t={t} acted={false} />
              </div>
            </li>);
        }
        // ⚠️ A COST IS THE TEST'S, NOT ONE RUN'S: the grader's lost-point item names the test. On
        // every run that did not pass, two failures of one file read as twice the points.
        const charged = t.runs.findIndex((y) => y.result !== "pass");
        return t.runs.map((x, j) => (
          <li className={rowClass(x.result)} key={`${t.id}-${j}`}>
            <span className="ttr-res"><Badge tone={RESULT[x.result]?.tone ?? "warn"}>
              {RESULT[x.result]?.label ?? x.result}</Badge></span>
            <div className="ttr-main">
              <div className="ttr-name">{t.kind === "code" ? <code>{x.name}</code> : x.name}</div>
              <div className="ttr-meta">{meta(t)}</div>
              {j === 0 && <Definition t={t}
                acted={t.runs.some((y) => y.result !== "absent") || t.source.trim() !== ""} />}
              {x.result !== "pass" && x.assertion && (
                <div className="ttr-out"><code>{x.assertion}</code></div>)}
              {j === charged && cost.length > 0 && (
                <div className="ttr-why">Counted under{" "}
                  {cost.map((c, k) => (
                    <span key={k}>{k > 0 && ", "}
                      {c.crit === here ? "this criterion"
                        : <a href={`#crit-${c.crit}`}>{c.crit}</a>}{" "}
                      −{c.points}</span>))}.</div>)}
              {t.integrity === "changed" && j === 0 && (
                <div className="ttr-out ttr-bad-box">{t.integrity_detail}</div>)}
              {j === 0 && t.changes.length > 0 && (
                <details className="rp-disc ttr-disc"><summary>What the grader changed</summary>
                  <ul className="ttr-changes">{t.changes.map((c, k) => (
                    <li key={k}><code>{c.from}</code> → <code>{c.to}</code>
                      {c.why && <span>{c.why}</span>}</li>))}</ul>
                </details>)}
              {/* 🔴 A TEST THE GRADER WROTE AND RAN, AND KEPT NOTHING OF (2026-10-05). [X] Both runs
                  after the scripts rule shipped stored names, commands and checks for eight
                  tests, and an empty `source` for every one: a pass nobody can re-read. Said, so
                  an empty disclosure is not mistaken for a test with nothing in it. */}
              {j === 0 && t.kind !== "code" && !t.source.trim()
                && t.runs.some((y) => y.result !== "absent") && (
                <div className="ttr-def small warn">The grader did not keep the test it ran, so
                  what this result rests on cannot be read here.</div>)}
              {j === 0 && t.source && (
                <details className="rp-disc ttr-disc">
                  <summary>{t.kind === "words" ? "The test it wrote"
                            : t.kind === "brief" ? "The tests it wrote" : "The test as it ran"}</summary>
                  <pre className="md-pre"><code>{t.source}</code></pre>
                </details>)}
            </div>
          </li>));
      })}
    </ul>);
}

/** The teacher's tests a criterion was given, inside that criterion's card (owner, 2026-10-05:
 *  "on report the test should be present on the respective criteria"). A test counts under one
 *  criterion and its failure is charged there, so that is where it is read. */
export function CriterionTests({ r, crit }: { r: Report; crit: string }) {
  const rows = (r.teacher_tests ?? []).filter((t) => t.criterion === crit);
  if (rows.length === 0) return null;
  return (
    <section className="ttr" aria-labelledby={`ttr-h-${crit}`}>
      {/* 🔴 "TESTS", NOT "YOUR TESTS" (owner, 2026-10-13): most are written by the grader from
          what the criterion credits; each row says who wrote it. */}
      <TestsHead rows={rows} id={`ttr-h-${crit}`} title="Tests" inCard />
      <TestRows r={r} rows={rows} here={crit} />
    </section>);
}

/**
 * The report's "Your tests" below the criteria: the tests that are in NO criterion's card.
 *
 * 🔴 NOTHING A RUN WAS GIVEN MAY DISAPPEAR. A grade made before 2026-10-05 records no criterion
 * for any test, so all of them are here, as they always were; a test whose criterion this grade
 * does not have is here too, rather than in a card nobody renders.
 */
export function TeacherTestsReport({ r }: { r: Report }) {
  const rows = r.teacher_tests ?? [];
  const cards = new Set(r.per_criterion.map((c) => c.id));
  const stray = rows.filter((t) => !t.criterion || !cards.has(t.criterion));
  if (stray.length === 0) return null;
  const commit = (r.repo.commit || "").slice(0, 7);
  return (
    <section className="ttr" id="tests" aria-labelledby="ttr-h">
      <TestsHead rows={stray} id="ttr-h"
        title={stray.length === rows.length ? "Tests" : "Tests on no criterion"}
        sub={commit && <> · run on the graded commit <code>{commit}</code> in the build jail</>} />
      <TestRows r={r} rows={stray} />
      <div className="ttr-foot small muted">The grader ran these on the commit it graded. Every
        change it made to your code is listed, and the console compares the assertions with
        yours: a changed assertion shows here in red and sends the grade to review.</div>
    </section>);
}
